import { Link } from "react-router-dom";
import { ArrowRight, Download, Mail, ChevronRight, Briefcase, Coffee, Users } from "lucide-react";
import SEO from "@/components/SEO";
import CorporateInquiryForm from "@/components/CorporateInquiryForm";
import { absoluteUrl } from "@/lib/seo-config";
import { track } from "@/lib/analytics";
import { BUSINESS } from "@/lib/constants";

const PROGRAMMES = [
  {
    key: "on-site",
    eyebrow: "Programme A",
    title: "On-site chair massage",
    Icon: Briefcase,
    description:
      "Licensed therapists travel to your office with a professional ergonomic chair. Each session is fully clothed, 10–20 minutes, focused on neck, shoulders, mid-back and forearms.",
    bullets: [
      "Quarterly wellness days or monthly recurring sessions",
      "Year-end stress relief and busy-quarter recoveries",
      "Insurance & reinsurance offices, law firms, fintech teams",
      "1–2 therapists on-site in 3-hour blocks",
      "Digital sign-up sheet for your HR lead",
    ],
  },
  {
    key: "packages",
    eyebrow: "Programme B",
    title: "In-clinic workplace packages",
    Icon: Users,
    description:
      "Your team books treatment time at our Hamilton clinic. The employer purchases a block of sessions, we handle scheduling, reminders and a quarterly utilisation summary back to HR.",
    bullets: [
      "60- or 90-minute Clinical Massage Therapy sessions",
      "Volume-discounted tiers, one employer invoice",
      "Anonymised utilisation & common-complaint reports",
      "Optional intake to flag staff needing extended care",
      "Sports teams, hospitality & aviation crews welcome",
    ],
  },
  {
    key: "workshops",
    eyebrow: "Programme C",
    title: "Lunch-and-learn workshops",
    Icon: Coffee,
    description:
      "Educational on-site sessions led by an Ashlan therapist. 45 minutes of practical content your team can use the same afternoon — no upsell, no fluff.",
    bullets: [
      "Desk Ergonomics That Don't Need a New Chair",
      "Five-Minute Recovery for Frequent Flyers",
      "The Posture-Pain Link: Self-Check and Self-Care",
      "Sport-Specific Recovery (custom-built for your team)",
      "Hybrid format available for remote staff",
    ],
  },
];

const SUBJECT = "Corporate Wellness Inquiry";

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Corporate Wellness", item: absoluteUrl("/corporate-wellness") },
  ],
};

export default function CorporateWellness() {
  const onDownload = () => {
    track("corporate_pdf_download", { source: "page" });
  };
  const onInquireClick = (programmeKey) => {
    track("corporate_inquire_click", { programme: programmeKey });
  };
  const inquireHref = "#inquiry-form";
  const mailHref = `mailto:${BUSINESS.email}?subject=${encodeURIComponent(SUBJECT)}`;

  return (
    <main data-testid="corporate-page" className="bg-white">
      <SEO
        title="Corporate Wellness — Workplace Massage Therapy, Bermuda"
        description="Three workplace wellness programmes from Ashlan Clinic — on-site chair massage, in-clinic packages billed to the employer, and lunch-and-learn workshops. Scoped to your team."
        path="/corporate-wellness"
        schemas={[breadcrumb]}
      />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <span className="ash-eyebrow">Corporate Wellness · Bermuda</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            Clinical massage therapy for the Bermuda workplace.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-3xl leading-relaxed">
            Three flexible programmes designed around your team&apos;s working day — chair massage on-site,
            in-clinic packages billed to the employer, and educational lunch-and-learn sessions on
            posture, stress and recovery. Outcome-focused, not spa.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="/downloads/ashlan-corporate-wellness.pdf"
              download
              onClick={onDownload}
              data-testid="corporate-download-pdf"
              className="ash-btn-primary"
            >
              <Download size={16} /> Download the programme brief
            </a>
            <a
              href={inquireHref}
              onClick={() => onInquireClick("hero")}
              data-testid="corporate-inquire-hero"
              className="ash-btn-secondary"
            >
              Talk to us about your team
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8" data-testid="corporate-programmes">
            {PROGRAMMES.map((p) => {
              const Icon = p.Icon;
              return (
                <div
                  key={p.key}
                  data-testid={`programme-${p.key}`}
                  className="bg-white border border-[#e5e7eb] p-8 md:p-10 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <Icon size={22} className="text-[#3A7A8C]" />
                    <span className="ash-eyebrow">{p.eyebrow}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#080808] tracking-tight leading-tight">
                    {p.title}
                  </h2>
                  <p className="mt-4 text-[#3C4048] leading-relaxed">{p.description}</p>
                  <ul className="mt-6 flex flex-col gap-3 text-[#3C4048] text-sm leading-relaxed">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex gap-2">
                        <ChevronRight size={14} className="text-[#5FA8B4] mt-1 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-8">
                    <a
                      href={inquireHref}
                      onClick={() => onInquireClick(p.key)}
                      data-testid={`programme-inquire-${p.key}`}
                      className="ash-link-underline inline-flex items-center"
                    >
                      Inquire about {p.title.toLowerCase()} <ArrowRight size={14} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <span className="ash-eyebrow">Investment</span>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
                Every quote is custom.
              </h2>
              <p className="mt-6 text-[#3C4048] leading-relaxed">
                Programmes are scoped to your team size, working schedule and the outcome
                you want — recovery support, posture rehab, team appreciation, or a recurring
                benefit. Insurance reimbursement is available through CG Insurance and Allshores
                for in-clinic sessions where eligible.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href={mailHref}
                  data-testid="corporate-email-cta"
                  className="ash-btn-primary"
                  onClick={() => onInquireClick("email")}
                >
                  <Mail size={16} /> Email info@ashlanclinic.com
                </a>
                <a
                  href={inquireHref}
                  onClick={() => onInquireClick("form")}
                  data-testid="corporate-inquire-bottom"
                  className="ash-btn-secondary"
                >
                  Send a brief via the form <ArrowRight size={16} />
                </a>
              </div>
            </div>
            <div className="border-l-2 border-[#5FA8B4] bg-[#F5F5F5] p-8 md:p-10">
              <span className="ash-eyebrow">What we&apos;ll send back</span>
              <ul className="mt-5 flex flex-col gap-4 text-[#3C4048] text-sm leading-relaxed">
                <li className="flex gap-2"><ChevronRight size={14} className="text-[#5FA8B4] mt-1 shrink-0" /><span>A tailored programme proposal with timing &amp; investment.</span></li>
                <li className="flex gap-2"><ChevronRight size={14} className="text-[#5FA8B4] mt-1 shrink-0" /><span>A short call with our lead therapist to confirm team goals.</span></li>
                <li className="flex gap-2"><ChevronRight size={14} className="text-[#5FA8B4] mt-1 shrink-0" /><span>Insurance &amp; eligibility check for in-clinic packages.</span></li>
                <li className="flex gap-2"><ChevronRight size={14} className="text-[#5FA8B4] mt-1 shrink-0" /><span>Sample reporting templates so HR knows what to expect.</span></li>
              </ul>
              <p className="mt-8 text-sm text-[#6B7280]">
                Response within one business day. Hamilton, Bermuda · (441) 292-6830.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* B6 + B7 — Advanced inquiry form */}
      <section
        id="inquiry-form"
        className="py-20 md:py-28 bg-[#F5F5F5] scroll-mt-24"
        data-testid="corporate-inquiry-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="ash-eyebrow">Tell us about your team</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#080808] leading-tight">
            Send us a brief — we&apos;ll come back with a scoped proposal.
          </h2>
          <p className="mt-6 text-[#3C4048] leading-relaxed max-w-2xl">
            The more we know about your team, working pattern and goals, the faster we can
            return a tailored programme. Required fields are marked with *. Everything else is
            optional but helps us scope accurately.
          </p>
          <div className="mt-10 bg-white border border-[#e5e7eb] p-6 md:p-10">
            <CorporateInquiryForm />
          </div>
        </div>
      </section>
    </main>
  );
}
